import ajax from "superagent";
import {
  Album,
  EventType,
  CreateLibraryFolderParamType,
  Folder,
  SrcType,
  DestFolderType
} from "../components/types";
import { addParamToURL } from "../../../utils/BrowserHelper";
import toastr from "../../common/toastr";
import {
  decodeEntityPath,
  getEncodedEntityPath,
  getEntitySegment,
  findEntityPath
} from "../components/FolderFeatures/EncryptionOfFolder";

export const fetchFoldersList = async () => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=root&content_type=library_folders&page=1`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Failed to fetch folders:", error);
    return [];
  }
};

export const fetchSubFolders = async (
  folderId: number,
  page: number = 1,
  type?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=LibraryFolder&${
        type === "LibraryFolderReference" ? "referenceId=" : "id="
      }${folderId}&content_type=library_folders&page=${page}&limit=50`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Failed to fetch subfolders:", error);
    return [];
  }
};

export const fetchFolderEvents = async (
  folderId: number,
  page: number = 1,
  type?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=LibraryFolder&${
        type === "LibraryFolderReference" ? "referenceId=" : "id="
      }${folderId}&content_type=galleries&page=${page}&limit=50`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Failed to fetch folder events:", error);
    return [];
  }
};

export const fetchEventList = async (
  page: number,
  setEvents: (val: EventType[], nextPage: number | null) => void,
  filter: string | null = "all"
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=root&content_type=galleries&page=${page}&event_timing_scope=${filter}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    const meta = data.meta;
    setEvents(data.entities, meta.nextPage);
  } catch (error) {
    console.error("Failed to fetch events:", error);
  }
};

export const fetchReferenceEntityDetails = async (
  entityId: number,
  encodedString: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/details?t=LibraryFolderReference&referenceId=${entityId}&p=${encodedString}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error(`Failed to fetch albums for event ${entityId}:`, error);
    return [];
  }
};

export const fetchEventBreadcrumbDetails = async (
  eventId: number,
  referenceId: number | null,
  encodedString: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/details?t=Gallery&${
        referenceId ? "referenceId" : "id"
      }=${referenceId || eventId}&p=${encodedString}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error(`Failed to fetch albums for event ${eventId}:`, error);
    return [];
  }
};

export const fetchAlbumDetails = async (
  albumId: number,
  encodedString: string
) => {
  if (!albumId) return;

  try {
    const paramName = "id";

    const response = await fetch(
      `/api/v1/photo-manager/details?t=Album&${paramName}=${albumId}&p=${encodedString}`
    );

    if (!response.ok) throw new Error("Failed to fetch album data");

    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Error fetching album details:", error);
  }
};

export const fetchGalleryContent = async (
  eventId: number,
  reference?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=Gallery&${
        reference ? "referenceId" : "id"
      }=${eventId}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error(`Failed to fetch albums for event ${eventId}:`, error);
    return [];
  }
};

export const fetchAlbumPhotos = async (albumId: number, page: number) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=Album&id=${albumId}&page=${page}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Failed to fetch photos for album ${albumId}:`, error);
    return [];
  }
};

export const fetchEventsByName = async (name: string) => {
  try {
    const response = await fetch(`/api/v1/photo-manager/search?q=${name}`);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Failed to fetch events with the name ${name}:`, error);
    return [];
  }
};

export const createGalleryAPI = async (
  name: string,
  folderId: number | null
): Promise<EventType> => {
  const response = await fetch("/api/v1/events/free", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      name: name.trim(),
      library_folder_id: folderId
    })
  });

  if (!response.ok) throw new Error("Failed to create event");

  const responseData = await response.json();
  if (!responseData.event) {
    throw new Error("Event property is missing in the response");
  }
  return responseData.event;
};

export const updateEventAPI = async (
  eventId: number,
  updates: {
    name?: string;
    library_folder_id?: number | null;
    publish_mode?: string;
    password?: string;
  }
): Promise<EventType> => {
  const response = await fetch(`/api/v1/events/${eventId}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ id: eventId, ...updates })
  });

  if (!response.ok) {
    throw new Error("Failed to update event");
  }

  const responseData = await response.json();
  if (!responseData.event) {
    throw new Error("Event property is missing in the response");
  }
  return responseData.event;
};

export const deleteEventAPI = async (eventId: number): Promise<void> => {
  const response = await fetch(`/api/v1/events/${eventId}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error("Failed to delete event");
  }
};

export const handleAddShortcut = async (
  sourceParentFolderId: number | null,
  targetParentFolderId: number | null,
  sourceEntityType: "LibraryFolder" | "Event",
  sourceEntityId: number,
  setLoading: (val: boolean) => void
) => {
  if (sourceParentFolderId === targetParentFolderId) {
    toastr.error("Cannot create shortcut in the same parent folder");
    return;
  }

  if (sourceParentFolderId === null && targetParentFolderId === null) {
    toastr.error("Root folders cannot create shortcuts in root directory");
    return;
  }

  setLoading(true);

  try {
    const requestBody = {
      parent_folder_id: targetParentFolderId,
      entity_type: sourceEntityType,
      entity_id: sourceEntityId
    };

    const response = await fetch("/api/v1/library_folder_references", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (response.ok && data.success) {
      toastr.success("Shortcut added successfully");
      return data.reference;
    } else {
      const errorMessage = data.error || "Failed to create shortcut";
      toastr.error(errorMessage);
    }
  } catch (error) {
    console.error("Error creating shortcut:", error);
    toastr.error("Failed to create shortcut. Please try again.");
  } finally {
    setLoading(false);
  }
};

export const deleteLibraryShortcut = async (referenceId: number) => {
  try {
    const res = await ajax.delete(
      `/api/v1/library_folder_references/${referenceId}`
    );

    if (res.body?.success) {
      return { success: true, message: res.body.message };
    } else {
      return { success: false, error: res.body.error || "Unknown error" };
    }
  } catch (error: any) {
    console.error("Failed to delete shortcut:", error);
    return {
      success: false,
      error: error?.response?.body?.error || "Something went wrong"
    };
  }
};

export const handleCreateAlbum = async (
  albumName: string,
  groupPhotos: boolean,
  selectedEventId: number,
  paramType: "referenceId" | undefined,
  groupPhotosEnabled: boolean,
  encodedPath: string,
  handleURLChange: () => void,
  onClose: () => void,
  eventData?: any,
  context?: any
): Promise<any> => {
  try {
    const paramKey = paramType === "referenceId" ? "reference_id" : "event_id";

    const response = await ajax.post(
      `/api/v1/albums?${paramKey}=${selectedEventId}&name=${albumName}${
        groupPhotosEnabled ? `&group_photos=${groupPhotos}` : ""
      }&p=${encodedPath}`
    );

    if (!response.ok) {
      throw new Error("Failed to create album");
    }

    const newAlbum = response.body.album;

    const albumSegment = getEntitySegment({
      id: newAlbum.id,
      referenceId: null,
      isReference: false,
      type: "Album"
    });

    // Find the parent event path for the album
    let parentEventPath = "";

    // If we're creating an album from the sidebar, construct the path to the parent event
    if (paramType !== "referenceId") {
      const eventSegment = `g:${selectedEventId}`;

      const isRootEvent = context?.eventsList?.some(
        event => event.id === selectedEventId
      );

      if (isRootEvent) {
        // Root event: completely ignore current path, just use the event segment
        parentEventPath = eventSegment;
      } else {
        // Nested event: use findEntityPath to get the proper folder hierarchy

        if (context?.rootFolders && context.rootFolders.length > 0) {
          const eventPath = findEntityPath(
            context.rootFolders,
            selectedEventId,
            "event"
          );

          if (eventPath) {
            parentEventPath = eventPath;
          } else {
            if (encodedPath && encodedPath !== "null") {
              const decodedPath = decodeEntityPath(encodedPath);

              if (
                decodedPath &&
                typeof decodedPath === "string" &&
                decodedPath.length > 0
              ) {
                const segments = decodedPath.split("/");
                const folderSegments = segments.filter(
                  segment =>
                    segment &&
                    typeof segment === "string" &&
                    segment.length > 2 &&
                    segment.includes(":") &&
                    !segment.startsWith("g:") &&
                    !segment.startsWith("a:")
                );

                if (folderSegments.length > 0) {
                  parentEventPath = [...folderSegments, eventSegment].join("/");
                } else {
                  parentEventPath = eventSegment;
                }
              } else {
                parentEventPath = eventSegment;
              }
            } else {
              parentEventPath = eventSegment;
            }
          }
        } else {
          parentEventPath = eventSegment;
        }
      }
    } else {
      // For reference events, use the reference segment
      const referenceSegment = `r:${selectedEventId}`;
      parentEventPath = referenceSegment;
    }

    const updatedPath = `${parentEventPath}/${albumSegment}`;
    const newEncodedPath = getEncodedEntityPath(updatedPath);

    const url = new URL(window.location.href);
    url.searchParams.set("p", newEncodedPath);
    window.history.pushState({}, "", url.toString());

    handleURLChange();

    onClose();

    return newAlbum;
  } catch (error) {
    console.error("Error creating album:", error);
    toastr.error("Unable to create album. Please try again", null, 1000);
    throw error;
  }
};

export const submitAlbumName = (
  e: any,
  albumId: number,
  albumName: string,
  hasInvalidatedValues: Function,
  setAlbumNameDrafts: Function,
  setAlbumsInEditMode: Function,
  onEditSuccess: (albums: Album[] | Album) => void,
  albumsData: Album[]
) => {
  e.stopPropagation();
  if (hasInvalidatedValues(albumId, true)) {
    return;
  }

  fetch(`/api/v1/album/${albumId}/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ name: albumName })
  })
    .then(response => {
      if (!response.ok) {
        const errorMsg =
          response.status === 404
            ? "Album not found."
            : "Failed to update album name.";
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      return response.json();
    })

    .then(updatedAlbum => {
      setAlbumNameDrafts((prev: any) => {
        const updatedNames = { ...prev };
        delete updatedNames[albumId];
        return updatedNames;
      });

      setAlbumsInEditMode((prev: any[]) =>
        prev.filter((id: number) => id !== albumId)
      );

      if (albumsData.length > 0) {
        const updatedAlbums = albumsData.map(album =>
          album.id === albumId
            ? { ...album, name: updatedAlbum.album.name }
            : album
        );
        onEditSuccess(updatedAlbums);
      } else {
        onEditSuccess(updatedAlbum.album);
      }
    })
    .catch(error => {
      console.error("Error while updating album name:", error);
    });
};

export const handleDelete = async (
  setLoading: Function,
  albumId: number,
  onDeleteSuccess: Function
) => {
  setLoading(true);
  try {
    const response = await ajax.delete(`/api/v1/albums/${albumId}`);
    if (!response.ok) {
      throw new Error("Failed to delete album");
    }
    onDeleteSuccess();
  } catch (error) {
    console.error("Error deleting album:", error);
    // @ts-ignore
    toastr.error("Unable to delete album. Please try again", null, 1000);
  } finally {
    setLoading(false);
  }
};

export const deletePhotos = async (
  selectedMediaId: Array<number>,
  onDeletePhotoSuccess: Function,
  selectedAlbumId: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photo-manager/delete_media_of_album`,
      type: "DELETE",
      data: {
        photo_ids: Array.from(selectedMediaId),
        album_id: selectedAlbumId
      }
    });

    // @ts-ignore
    toastr.success("Successfully deleted");
    onDeletePhotoSuccess();

    // Refresh the photos after deletion
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to delete these photos.");
    } else {
      // @ts-ignore
      toastr.error("Failed to delete photos. Please try again");
    }
    console.error(err);
  }
};

export const handleSave = (
  photoId: number,
  userId: number,
  onSuccess: Function
) => {
  ajax
    .post(addParamToURL(`/api/v1/photos/${photoId}/save`, "user_id", userId))
    .then(res => {
      onSuccess(true);
      //@ts-ignore
      toastr.success("Photo saved", null, { timeOut: 1000 });
    });

  return true;
};

export const handleUnsave = (
  photoId: number,
  userId: number,
  onSuccess: Function
) => {
  ajax
    .delete(
      addParamToURL(`/api/v1/photos/${photoId}/unsave`, "user_id", userId)
    )
    .then(res => {
      onSuccess(false);
      //@ts-ignore
      toastr.success("Photo unsaved", null, { timeOut: 1000 });

      return true;
    });
};

export const hidePhotos = async (
  selectedMediaId: any,
  hideOrUnhideText: string,
  selectedEventId: number,
  clearSelection: Function,
  fetchAlbumPhotosFunction: Function,
  selectedAlbum: any,
  photoCurrentPage: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photos/${hideOrUnhideText}`,
      type: "POST",
      data: {
        photo_ids: Array.from(selectedMediaId),
        event_id: selectedEventId
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
    clearSelection();
    // Refresh the photos to get updated hidden status
    if (selectedAlbum) {
      fetchAlbumPhotosFunction(selectedAlbum, photoCurrentPage);
    } else {
    }
  } catch (err) {
    // @ts-ignore
    toastr.error("Failed to update");
    console.error(err);
  }
};

export const hidePhoto = async (
  selectedMediaId: any,
  hideOrUnhideText: string,
  selectedEventId: number,
  onSuccess: Function,
  selectedAlbum: any,
  fetchAlbumPhotosFunction: Function,
  photoCurrentPage: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photos/${hideOrUnhideText}`,
      type: "POST",
      data: {
        photo_ids: [selectedMediaId],
        event_id: selectedEventId
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
    // Refresh the photos to get updated hidden status
    onSuccess(hideOrUnhideText === "hide" ? true : false);
    if (selectedAlbum) {
      fetchAlbumPhotosFunction(selectedAlbum, photoCurrentPage);
    }
  } catch (err) {
    // @ts-ignore
    toastr.error("Failed to update");
    console.error(err);
  }
};

export const handleDownload = async (
  selectedMediaId: Set<number>,
  selectedEventId: number
) => {
  if (selectedMediaId.size === 0) return;

  const photoIds = Array.from(selectedMediaId);
  const params = new URLSearchParams();
  params.append("t", "Gallery");
  params.append("id", selectedEventId.toString());
  photoIds.forEach(id => params.append("photo_ids[]", id.toString()));
  const url = `/api/v1/photo-manager/download?${params.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch download URL");

    const data = await response.json();
    if (data.download_url) {
      window.location.href = data.download_url; // Trigger ZIP download
    }
  } catch (error) {
    console.error("Download failed:", error);
    // @ts-ignore
    toastr.error("Failed to download photos. Please try again", null, 1000);
  }
};

export const fetchEventData = async (
  eventId: number,
  setSelectedEvent: (event: any) => void,
  setLoadingEvent: (loading: boolean) => void
) => {
  try {
    setLoadingEvent(true);
    const response = await fetch(
      `/api/v1/photo-manager/details/?t=Gallery&id=${eventId}`
    );

    if (!response.ok) throw new Error("Failed to fetch event data");

    const data = await response.json();
    setSelectedEvent(data.entities);
  } catch (error) {
    console.error("Error fetching event details:", error);
  } finally {
    setLoadingEvent(false);
  }
};

export const fetchAlbumData = async (
  albumId: number,
  setSelectedAlbum: (album: any) => void,
  setLoadingAlbum: (loading: boolean) => void
) => {
  try {
    setLoadingAlbum(true);
    const response = await fetch(
      `/api/v1/photo-manager/details/?t=Album&id=${albumId}`
    );

    if (!response.ok) throw new Error("Failed to fetch album data");

    const data = await response.json();
    setSelectedAlbum(data.entities);
  } catch (error) {
    console.error("Error fetching album details:", error);
  } finally {
    setLoadingAlbum(false);
  }
};
export const createLibraryFolder = async (
  params: CreateLibraryFolderParamType,
  onCreateLibraryFolderSuccess: (newLibraryFolder: Folder) => void
) => {
  try {
    const response = await $.ajax({
      url: `/api/v1/library_folders`,
      type: "POST",
      data: params
    });
    const newLibraryFolder = response.library_folder;
    onCreateLibraryFolderSuccess(newLibraryFolder);
  } catch (error) {
    console.error("Error creating library folder:", error);
    // @ts-ignore
    toastr.error(
      "Unable to create library folder. Please try again",
      null,
      1000
    );
  }
};

export const deleteFolder = async (folderId: number): Promise<boolean> => {
  try {
    const response = await $.ajax({
      url: `/api/v1/library_folders/${folderId}`,
      type: "DELETE"
    });

    // @ts-ignore
    toastr.success("Successfully deleted");
    return true;
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to delete this folder.");
    } else {
      // @ts-ignore
      toastr.error("Failed to delete folder. Please try again");
    }

    return false;
  }
};

export const updateFolder = async (
  folderId: number,
  name: string,
  parentFolderId: number | null
) => {
  try {
    await $.ajax({
      url: `/api/v1/library_folders/${folderId}`,
      type: "PATCH",
      data: {
        library_folder: {
          name: name,
          parent_folder_id: parentFolderId
        }
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to update this folder.");
    } else {
      // @ts-ignore
      toastr.error("Failed to update folder. Please try again");
    }
    console.error(err);
  }
};

export const moveFolder = async ({
  setLoading,
  src,
  dest
}: {
  setLoading: (val: boolean) => void;
  src: SrcType;
  dest: DestFolderType;
}) => {
  setLoading(true);

  try {
    const requestBody = {
      src: [src.entityType, src.entityId],
      dest: [dest.entityType, dest.entityId]
    };

    const response = await fetch("/api/v1/photo-manager/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.ok) {
      toastr.success("Moved successfully");
    } else {
      toastr.error("Failed to move");
    }
  } catch (error) {
    console.error("Error Moving:", error);
    toastr.error("Failed to Move. Please try again.");
  } finally {
    setLoading(false);
  }
};
